import _ from "lodash";
import {Fragment} from 'react';
import {isValidPhoneNumber} from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import Select from 'react-select';
import {Role} from "../../types/Role";
import {User} from "../../types/User";
import {ApiUtility} from "../common/ApiUtility";
import {CheckBox} from "../common/CheckBox";
import {SelectOption} from "../common/CommonUIElementTypes";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";
import {Constants} from "../common/Constants";
import {EmailUtility} from "../common/EmailUtility";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PillButton} from "../common/PillButton";
import {SelectUtility} from "../common/SelectUtility";
import {StringUtility} from "../common/StringUtility";

interface UserFormProps {
    user?: User;
    save: Function;
    cancel: Function;
    isNew?: boolean;
}

interface UserFormState {
    username: string;
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    role_id?: number;
    active: boolean;
    password: string;
    confirm_password: string;

    loading: boolean;
    showUnacceptedInviteWarning: boolean;
    showInviteNotSentWarning: boolean;
    showConfirmResetPasswordModal: boolean;
}

export let UserForm = function (props: UserFormProps) {
    const user = _.clone(props.user) || undefined;
    const initialState: UserFormState = {
        username: user?.username || '',
        first_name: user?.person?.first_name || '',
        last_name: user?.person?.last_name || '',
        phone: user?.person?.phone || '',
        email: user?.person?.email || '',
        role_id: (user?.user_roles || [])[0]?.role_id,
        active: user?.active ?? true,
        password: props.isNew ? '' : Constants.passwordPlaceholder,
        confirm_password: props.isNew ? '' : Constants.passwordPlaceholder,

        showUnacceptedInviteWarning: !!(user && !user.user_invite_accepted),
        showInviteNotSentWarning: !!(user && !user.user_invite_sent),
        loading: false,
        showConfirmResetPasswordModal: false,
    };

    const fetchSupportingData = function () {
        return ApiUtility.getRolesList()
            .then(function (roles: Role[]) {
                return Promise.resolve({
                    supportingData: {
                        roleOptions: SelectUtility.modelArrayToSelectOptions(roles || []),
                    }
                });
            })
    }

    const formEngine = useModelFormEngine(
        initialState, user, fetchSupportingData, validateForm, formStateToSaveData, props.save
    );

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve, reject) {
            let validationErrors = [];
            let invalidFields = [];

            Promise.resolve({})
                .then(function () {
                    if (formEngine.dataStore.data().email) {
                        if (!EmailUtility.isValid(formEngine.dataStore.data().email)) {
                            validationErrors.push('Please enter a valid email address.');
                            invalidFields.push('email');
                        }

                        if (
                            user &&
                            user.person &&
                            user.person.email &&
                            formEngine.dataStore.data().email === user.person.email
                        ) {
                            // Don't validate email availability if it's an existing user being updated who already
                            // has that email address.
                            return Promise.resolve();
                        } else {
                            return ApiUtility.getIsPersonEmailAvailable(formEngine.dataStore.data().email)
                                .then(function (emailIsAvailable: boolean) {
                                    if (!emailIsAvailable) {
                                        validationErrors.push('Another user is already using that email address - please enter a different email address.');
                                        invalidFields.push('email');
                                    }
                                    return Promise.resolve();
                                });
                        }
                    } else {
                        validationErrors.push('Please enter an email address.');
                        invalidFields.push('email');
                        return Promise.resolve();
                    }
                })
                .then(function () {
                    if (!formEngine.dataStore.data().first_name) {
                        validationErrors.push('Please enter a first name.');
                        invalidFields.push('first_name');
                    }

                    if (!formEngine.dataStore.data().last_name) {
                        validationErrors.push('Please enter a last name.');
                        invalidFields.push('last_name');
                    }

                    if (!formEngine.dataStore.data().phone) {
                        validationErrors.push('Please enter a phone number.');
                        invalidFields.push('phone');
                    } else {
                        if (!isValidPhoneNumber(formEngine.dataStore.data().phone)) {
                            validationErrors.push('Please enter a valid phone number.');
                            invalidFields.push('phone');
                        }
                    }

                    if (!formEngine.dataStore.data().role_id) {
                        validationErrors.push('Please select a role.');
                        invalidFields.push('role_id');
                    }

                    if (
                        !props.isNew &&
                        (formEngine.dataStore.data().password !== Constants.passwordPlaceholder ||
                            formEngine.dataStore.data().confirm_password !== Constants.passwordPlaceholder)
                    ) {
                        if (formEngine.dataStore.data().password.length < Constants.minPasswordLength) {
                            validationErrors.push(
                                `Passwords must be at least ${Constants.minPasswordLength} characters long.`
                            );
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (!StringUtility.containsUpperCaseLetter(formEngine.dataStore.data().password)) {
                            validationErrors.push('Passwords must contain at least one upper case letter.');
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (!StringUtility.containsNumber(formEngine.dataStore.data().password)) {
                            validationErrors.push('Passwords must contain at least one number.');
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (!StringUtility.containsSpecialCharacter(formEngine.dataStore.data().password)) {
                            validationErrors.push(
                                'Passwords must contain at least one of these characters: ' +
                                '!$%^&*()_+|~-=`{}[]:";\'<>?,./'
                            );
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (formEngine.dataStore.data().password !== formEngine.dataStore.data().confirm_password) {
                            validationErrors.push('Password does not match confirm password.');
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }
                    }

                    return handleValidationResult(validationErrors, invalidFields, resolve);
                });
        });
    }

    function formStateToSaveData(formStateToTransform: UserFormState) {
        let saveData = _.clone(formStateToTransform);
        return saveData;
    }

    function handleRoleChange(fieldName: string, option: SelectOption) {
        formEngine.updateAndRenderUnsavedData({[fieldName]: option.value});
    }

    function onClickResetPassword() {
        formEngine.dataStore.updateAndRender({showConfirmResetPasswordModal: true})
    }

    function closeConfirmResetPasswordModal() {
        formEngine.dataStore.updateAndRender({showConfirmResetPasswordModal: false})
    }

    function sendResetPasswordEmail() {
        formEngine.dataStore.updateAndRender({loading: true})
        ApiUtility.sendPasswordResetEmail({email: formEngine.dataStore.data().email})
            .then(() => {
                formEngine.dataStore.updateAndRender({
                    loading: false,
                    showConfirmResetPasswordModal: false
                })
                MessageDisplayerUtility.success('Successfully sent password reset email.');
            })
            .catch((err: object) => {
                formEngine.dataStore.updateAndRender({loading: false})
                console.log("Error sending password reset email: ", err);
                MessageDisplayerUtility.error('Error sending password reset email.');
            });
    }

    function resendActivationEmail() {
        if (formEngine.dataStore.data().loading) {
            return;
        }
        let sendSuccessMessage = 'Successfully resent activation email.';
        let sendErrorMessage = 'Error resending activation email.';
        if (formEngine.dataStore.data().showInviteNotSentWarning && user?.acumatica_id) {
            sendSuccessMessage = 'Successfully sent invitation email.';
            sendErrorMessage = 'Error sending invitation email.';
        }
        formEngine.dataStore.updateAndRender({loading: true})
        ApiUtility.resendActivationEmail({id: user?.id})
            .then(() => {
                formEngine.dataStore.updateAndRender({loading: false, showInviteNotSentWarning: false})
                MessageDisplayerUtility.success(sendSuccessMessage);
            })
            .catch((err: object) => {
                formEngine.dataStore.updateAndRender({loading: false})
                console.log("Error sending activation email: ", err);
                MessageDisplayerUtility.error(sendErrorMessage);
            });
    }

    let inviteWarningHeaderClasses = 'unaccepted-invite-warning-header';
    let inviteWarningIconSource = '/static/images/warning-light-gray.svg';
    let inviteWarningText = 'User invite sent. User account still inactive.';
    let activationButtonText = 'RESEND ACTIVATION EMAIL';
    if (formEngine.dataStore.data().showInviteNotSentWarning) {
        inviteWarningHeaderClasses += ' invite-not-sent';
        inviteWarningIconSource = '/static/images/paper-airplane-textcolor.svg';
        inviteWarningText = 'User invite not yet sent. User account still inactive.';
        if (user?.acumatica_id) {
            activationButtonText = 'SEND INVITATION';
        }
    }

    return (
        <Fragment>
            <ConditionalRenderer
                if={formEngine.dataStore.data().showUnacceptedInviteWarning}
                renderer={() => (
                    <div className={inviteWarningHeaderClasses}>
                        <div className="unaccepted-invite-warning-header-content">
                            <img
                                src={inviteWarningIconSource}
                                className="unaccepted-invite-warning-icon"
                            />
                            <div className="unaccepted-invite-warning-text">
                                {inviteWarningText}
                            </div>
                        </div>
                    </div>
                )}
            />
            <ConditionalRenderer
                if={!!user && (user.subsequent_auto_sync_failures || 0) >= 3 && !!user.acumatica_id}
                renderer={() => (
                    <div className="user-form-auto-sync-disabled-banner">
                        <div className="user-form-auto-sync-disabled-banner-content">
                            <img
                                src="/static/images/warning-triangle-light-gray.svg"
                                className="user-form-auto-sync-disabled-banner-icon"
                            />
                            <div className="user-form-auto-sync-disabled-banner-text">
                                Automatic sync is disabled for this user due to repeated errors. Successfully manually
                                syncing this user will re-enable auto sync.
                            </div>
                        </div>
                    </div>
                )}
            />
            <div className="common-form-container">
                <ConditionalRenderer
                    if={formEngine.dataStore.data().loading}
                    renderer={() => (<OverlayLoadingIndicator/>)}
                />
                <ConditionalRenderer
                    if={formEngine.dataStore.data().showConfirmResetPasswordModal}
                    renderer={() => (
                        <ConfirmCancelModal
                            warningMessage="This will send the user an email allowing them to reset their password."
                            headerText="Confirm Send Reset Password Email"
                            cancel={closeConfirmResetPasswordModal}
                            confirm={sendResetPasswordEmail}
                            confirmButtonText="SEND EMAIL"
                        />
                    )}
                />
                <div className="common-form narrow-form user-form">
                    <ConditionalRenderer if={formEngine.dataStore.data().supportingData} renderer={() => (
                        <div className="common-form-body">
                            <div className="common-form-body-row">
                                <div className={formEngine.getFormFieldClasses("common-form-field", "email")}>
                                    <div className="common-form-field-label">
                                        Username / Email
                                    </div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="email"
                                            type="text"
                                            className="common-form-input"
                                            value={formEngine.dataStore.data().email}
                                            onChange={(event) => formEngine.handleTextFieldChange("email", event)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="common-form-body-row">
                                <div className="common-form-body-row-horizontal">
                                    <div className="common-form-body-row-horizontal-cell">
                                        <div
                                            className={formEngine.getFormFieldClasses("common-form-field", "first_name")}>
                                            <div className="common-form-field-label">
                                                Name
                                            </div>
                                            <div className="common-form-field-input-container">
                                                <input
                                                    id="first_name"
                                                    type="text"
                                                    className="common-form-input"
                                                    value={formEngine.dataStore.data().first_name}
                                                    onChange={(event) => formEngine.handleTextFieldChange("first_name", event)}
                                                />
                                            </div>
                                            <div className="common-form-field-sublabel">
                                                First Name
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-form-body-row-horizontal-cell">
                                        <div
                                            className={formEngine.getFormFieldClasses("common-form-field", "last_name")}>
                                            <div className="common-form-field-label">
                                            </div>
                                            <div className="common-form-field-input-container">
                                                <input
                                                    id="last_name"
                                                    type="text"
                                                    className="common-form-input"
                                                    value={formEngine.dataStore.data().last_name}
                                                    onChange={(event) => formEngine.handleTextFieldChange("last_name", event)}
                                                />
                                            </div>
                                            <div className="common-form-field-sublabel">
                                                Last Name
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="common-form-body-row">
                                <div className="common-form-body-row-horizontal">
                                    <div className="common-form-body-row-horizontal-cell">
                                        <div
                                            className={formEngine.getFormFieldClasses("common-form-field phone-field", "phone")}>
                                            <div className="common-form-field-label">
                                                Phone Number
                                            </div>
                                            <div className="common-form-field-input-container">
                                                <PhoneInput
                                                    country="US"
                                                    value={formEngine.dataStore.data().phone}
                                                    className="common-form-input"
                                                    onChange={(value) => formEngine.handleTextFieldChange("phone", {target: {value: '' + value}})}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-form-body-row-horizontal-cell">
                                        <div
                                            className={formEngine.getFormFieldClasses("common-form-field role-select", "role_id")}>
                                            <div className="common-form-field-label">
                                                User Role
                                            </div>
                                            <div className="common-form-field-input-container">
                                                <Select
                                                    className="common-form-select"
                                                    classNamePrefix="common-form-select"
                                                    value={SelectUtility.selectOptionForId(
                                                        '' + formEngine.dataStore.data().role_id,
                                                        formEngine.dataStore.data().supportingData.roleOptions
                                                    )}
                                                    onChange={(option) => handleRoleChange(
                                                        'role_id',
                                                        option as SelectOption
                                                    )}
                                                    options={formEngine.dataStore.data().supportingData.roleOptions}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <ConditionalRenderer
                                if={!props.isNew && !formEngine.dataStore.data().showUnacceptedInviteWarning}
                                renderer={() => (
                                    <div className="common-form-body-row">
                                        <div className="common-form-body-row-horizontal">
                                            <div className="common-form-body-row-horizontal-cell">
                                                <div
                                                    className={formEngine.getFormFieldClasses("common-form-field", "password")}>
                                                    <div className="common-form-field-label">
                                                        password
                                                    </div>
                                                    <div className="common-form-field-input-container">
                                                        <input
                                                            id="password"
                                                            type="password"
                                                            className="common-form-input"
                                                            value={formEngine.dataStore.data().password}
                                                            onChange={(event) => formEngine.handleTextFieldChange("password", event)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="common-form-body-row-horizontal-cell">
                                                <div
                                                    className={formEngine.getFormFieldClasses("common-form-field", "confirm_password")}>
                                                    <div className="common-form-field-label">
                                                        confirm password
                                                    </div>
                                                    <div className="common-form-field-input-container">
                                                        <input
                                                            id="confirm_password"
                                                            type="password"
                                                            className="common-form-input"
                                                            value={formEngine.dataStore.data().confirm_password}
                                                            onChange={(event) => formEngine.handleTextFieldChange("confirm_password", event)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            />

                            <ConditionalRenderer
                                if={!props.isNew && !formEngine.dataStore.data().showUnacceptedInviteWarning}
                                renderer={() => (
                                    <div className="common-form-body-row active-checkbox-row">
                                        <CheckBox
                                            id="user-form-active-checkbox"
                                            checked={formEngine.dataStore.data().active}
                                            onChange={(checked: boolean) => formEngine.handleCheckboxChange("active", checked)}
                                            label="Active"
                                        />
                                    </div>
                                )}
                            />

                            <ConditionalRenderer
                                if={!props.isNew && !formEngine.dataStore.data().showUnacceptedInviteWarning}
                                renderer={() => (
                                    <div className="common-form-body-row">
                                        <div className="common-form-field">
                                            <div className="common-form-field-input-container">
                                                <PillButton
                                                    onClick={onClickResetPassword}
                                                    buttonClasses="solid-black-button reset-password-button"
                                                    buttonText="SEND RESET PASSWORD EMAIL"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            />

                            <ConditionalRenderer
                                if={!props.isNew && formEngine.dataStore.data().showUnacceptedInviteWarning}
                                renderer={() => (
                                    <div className="common-form-body-row">
                                        <div className="common-form-field">
                                            <div className="common-form-field-label">
                                                Activation
                                            </div>
                                            <div className="common-form-field-input-container">
                                                <PillButton
                                                    onClick={resendActivationEmail}
                                                    buttonClasses="solid-black-button resend-activation-button"
                                                    buttonText={activationButtonText}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            />

                            <div className="common-form-body-row">
                                <div className="common-form-action-buttons-row">
                                    <PillButton
                                        containerClasses="common-form-action-button-container"
                                        buttonClasses="solid-green-button submit-button"
                                        onClick={formEngine.onClickSave}
                                        buttonText={props.isNew ? "INVITE" : "UPDATE"}
                                    />
                                    <PillButton
                                        containerClasses="common-form-action-button-container"
                                        buttonClasses="green-border-button cancel-button"
                                        onClick={() => props.cancel()}
                                        buttonText="CANCEL"
                                    />
                                </div>
                            </div>
                        </div>
                    )}/>
                </div>
                <LeaveWithoutSavingWarningModal navigationBlocker={formEngine.navigationBlocker}/>
            </div>
        </Fragment>
    );
};
